import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import * as moment from 'moment';
import { connect } from "react-redux";
import { Dispatch } from 'redux';

import { CardController } from 'src/app/shared/card.controller';
import { ToastNotifications } from 'src/core/ui/toast';
import { FormatService } from 'src/core/services/format.service';
import { ContractsDateBandView } from './contract-dateband.view';
import { ContextService } from 'src/core/services/context.service';
import { ContractRangeDateListDataService } from 'src/app/products/contract/form/contract-range-date.data';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	dates: ContractRangeDateItem[];
}

interface PropsFromDispatch {
	datesLoaded: (contractItem: ContractItem, dates: ContractRangeDateItem[]) => any;
}

interface Props extends ComponentViewProps<ContractItem> {
	mode: ControlMode;
	onModeChanged: (mode: ControlMode) => void;
	contextService: ContextService;
	formatService: FormatService;
	product: ProductItem | TourItineraryItem;
	updatePriceTable: () => void;
}

@observer
class ContractsDateBandController extends React.Component<Props & PropsFromState & PropsFromDispatch, {}> {
	@observable
	protected dataService: ContractRangeDateListDataService;

	@resolve
	protected toast: ToastNotifications;

	componentDidMount() {
		this.dataService = ContractRangeDateListDataService.make(this.props.contextService, this.props.formatService);
		this.dataService.addFilter('contractId', 'eq', this.props.model.id);
		this.dataService.addSorting('bookingDateFrom', 'asc', true);
		this.dataService.pageSize = 20;
		this.dataService.items = (this.props.dates || []).slice();
	}

	@autobind
	protected createFrom() {
		let bookingDateFrom: Date;
		let bookingDateTo: Date;
		let serviceDateFrom: Date;
		let serviceDateTo: Date;
		if (this.dataService.items.length > 0) {
			bookingDateFrom = moment.utc(this.dataService.items[0].bookingDateFrom).toDate();
			bookingDateTo = moment.utc(this.dataService.items[0].bookingDateTo).toDate();
			const oldDateTo = this.dataService.items[this.dataService.items.length - 1].serviceDateTo;
			serviceDateFrom = moment.utc(oldDateTo).add(1, 'day').toDate();
			serviceDateTo = moment.utc(oldDateTo).add(3, 'months').toDate();
		} else {
			bookingDateFrom = moment.utc(new Date()).startOf('year').toDate();
			bookingDateTo = moment.utc(bookingDateFrom).add(3, 'months').subtract(1, 'day').toDate();
			serviceDateFrom = bookingDateFrom;
			serviceDateTo = bookingDateTo;
		}
		const range = this.dataService.add(bookingDateFrom, bookingDateTo, serviceDateFrom, serviceDateTo, this.props.model.id);
		return range;
	}

	@autobind
	private async createDates() {
		this.props.setBusy(true);
		const range = await this.dataService.create(() => this.createFrom());
		this.props.datesLoaded(this.props.model, [range]);
		this.props.setBusy(false);

		return range;
	}

	@autobind
	private async updateCostPrice(range: ContractRangeDateItem, updatePrice: (i: PriceElement) => void) {
		const cost: CostItem = this.props.model.dynamicProperties['cost'];
		const price: PriceItem = this.props.model.dynamicProperties['price'];
		const costToUpdate = cost.elements.filter(e => moment.utc(e.bookingDateFrom).isSame(range.bookingDateFrom, 'day')
			&& moment.utc(e.bookingDateTo).isSame(range.bookingDateTo, 'day')
			&& moment.utc(e.serviceDateFrom).isSame(range.serviceDateFrom, 'day')
			&& moment.utc(e.serviceDateTo).isSame(e.serviceDateTo));
		const sellToUpdate = price.elements.filter(e => moment.utc(e.bookingDateFrom).isSame(range.bookingDateFrom, 'day')
			&& moment.utc(e.bookingDateTo).isSame(range.bookingDateTo, 'day')
			&& moment.utc(e.serviceDateFrom).isSame(range.serviceDateFrom, 'day')
			&& moment.utc(e.serviceDateTo).isSame(e.serviceDateTo));

		for (const i of costToUpdate.concat(sellToUpdate)) {
			updatePrice(i);
		}
		this.props.updatePriceTable();
	}

	render() {
		if (this.dataService == null) {
			return null;
		}

		return (
			<CardController
				icon="calendar"
				title="Date Bands"
				mode={this.props.mode}
				className="card-borderless"
			>
				<ContractsDateBandView
					mode={this.props.mode}
					model={this.dataService}
					setBusy={this.props.setBusy}
					createDates={this.createDates}
					contract={this.props.model}
					updateCostPrice={this.updateCostPrice}
					product={this.props.product}
				/>
			</CardController>
		);
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: ComponentViewProps<ContractItem>
): PropsFromState => {
	const contract = state.otherLoadedEntities.find(e => e.entity == props.model.entity) as ContractItem;
	return {
		dates: contract.dateBands,
	};
};

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	datesLoaded: (
		contract: ContractItem,
		dates: ContractRangeDateItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(contract, dates, "dateBands")),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(ContractsDateBandController);
export { connected as ContractsDateBandController };



// WEBPACK FOOTER //
// ./src/app/products/contract/form/parts/contract-dateband.controller.tsx